<template>
  <avs-card class="pb-4">
    <template v-slot:title>
      <v-toolbar
        class="mt-4 custom-toolbar"
        elevation="0"
      >
        <v-text-field
          class="mr-8"
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
        />

        <avs-createButton
          :spantext="$t('actions.create', { entity: $t('commons.fieldtype').toLowerCase() })"
          menu
          :name="$t('actions.new', { entity: $t('commons.fieldtype').toLowerCase() })"
        >
          <template v-slot:list>
            <v-list>
              <v-list-item
                v-for="(item, index) in basecomponents"
                :key="index"
                link
                @click="$event => handleOpenEditDialogByComponent(item)"
              >
                <v-list-item-title>{{ get_type(item.name) }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </template>
        </avs-createButton>
      </v-toolbar>
    </template>
    <template v-slot:content>
      <v-card-text>
        <v-alert
          color="secondary"
          type="info"
          outlined
          dismissible
        >
          <div class="text-h6">{{ $t("commons.tips") }}</div>
          <div>{{ $t("messages.information.double_clicks") }}</div>
          <div>{{ $t("views.template.published_item_edit") }}</div>
        </v-alert>
      </v-card-text>

      <v-data-table
        class="ma-10"
        dense
        :headers="headers"
        :items="items"
        :items-per-page="-1"
        :search="search"
        item-key="id"
        @dblclick:row="
          ($event, { item }) => {
            if (allow.duplicate(item)) {
              handleOpenEditDialog(item)
            }
          }
        "
      >
        <template #item.created_at="{ item }">
          {{ toDatetime(item.created_at) }}
        </template>
        <template #item.component.name="{ item }">
          {{ get_type(item.component.name) }}
        </template>
        <template v-slot:item.status="{ item }">
          <avs-chip :label="false">
            {{ get_status(item.status) }}
          </avs-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <avs-editButton
              :disabled="!allow.edit(item)"
              @click.stop="handleOpenEditDialog(item)"
              :spantext="$t('actions.edit', { entity: $t('commons.fieldtype').toLowerCase() })"
            />
            <avs-duplicateButton
              :disabled="!allow.duplicate(item)"
              @click.stop="duplicateField(item)"
              :spantext="$t('actions.duplicate', { entity: $t('commons.fieldtype').toLowerCase() })"
            />

            <avs-deleteButton
              :disabled="!allow.delete(item)"
              @click.stop="handleOpenDeleteDialog(item)"
              :spantext="$t('actions.delete', { entity: $t('commons.fieldtype').toLowerCase() })"
            />
          </div>
        </template>
      </v-data-table>

      <v-dialog
        v-model="editDialogIsOpen"
        width="40vw"
      >
        <component
          v-model="selectedItem"
          :component="selectedComponent"
          :hasUniqueName="hasUniqueName"
          :is="editDialogContent"
          :isEditable="isEditable"
          @cancel="handleCancel"
          @error="handleError"
          @success="handleSuccess"
        />
      </v-dialog>

      <DeleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="FIELDTYPE"
      />
    </template>
  </avs-card>
</template>

<script>
import { toDatetime } from "@/utils/datetime"
import { FieldTypeService } from "@/services/documents"
import { COMPONENT_ENUM, STATE_ENUM } from "@/utils/consts"
import { mkUnique } from "@/utils/validator"

import DeleteDialog from "@/components/generics/dialogs/DeleteDialog.vue"
import SelectorFieldEditor from "@/components/ri3/documents/fieldtypes/SelectorFieldEditor.vue"
import TableFieldEditor from "@/components/ri3/documents/fieldtypes/TableFieldEditor.vue"

export default {
  name: "FieldTypeListView",
  components: { DeleteDialog, SelectorFieldEditor, TableFieldEditor },
  created() {
    this.fetchFieldTypes()
  },
  computed: {
    hasUniqueName: {
      get() {
        const names = this.items.filter(e => e.name !== this.selectedItem?.name).map(e => e.name)
        return mkUnique(names, "name")
      }
    }
  },
  data() {
    return {
      allow: {
        duplicate: v => v.state.id != STATE_ENUM.LOCKED.id,
        edit: v => v.state.id === STATE_ENUM.EDITABLE.id,
        delete: v => v.state.id === STATE_ENUM.EDITABLE.id
      },

      headers: [
        {
          text: this.$t("commons.name"),
          value: "name",
          class: "min-width-150",
          sortable: true
        },
        {
          text: this.$t("commons.type"),
          value: "component.name",
          align: "center",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("commons.created_the"),
          value: "created_at",
          class: "min-width-150",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("commons.status.name"),
          value: "status",
          align: "center",
          sortable: true,
          filterable: true
        },
        {
          text: this.$t("commons.actions"),
          value: "actions",
          align: "right",
          width: 150,
          sortable: false,
          filterable: false
        }
      ],
      isEditable: true,
      items: [],
      search: "",

      basecomponents: Object.values(COMPONENT_ENUM),
      deleteDialogIsOpen: false,
      editDialogIsOpen: false,
      editDialogContent: null,
      selectedItem: undefined,
      selectedComponent: null
    }
  },
  methods: {
    toDatetime,
    fetchFieldTypes() {
      let companyId = this.$store.getters["app/getCompany"].pk
      FieldTypeService.getAll(companyId).then(response => {
        this.items = response
        this.items.map(v => {
          v["status"] = v.state.label
        })
      })
    },
    handleOpenDeleteDialog(item) {
      this.selectedItem = item
      this.deleteDialogIsOpen = true
    },
    handleOpenEditDialog(item) {
      this.selectedItem = item
      this.isEditable = item.state.id === STATE_ENUM.EDITABLE.id
      this.handleOpenEditDialogByComponent(this.selectedItem.component)
    },
    handleOpenEditDialogByComponent(component) {
      this.selectedComponent = component
      switch (this.selectedComponent.id) {
        case COMPONENT_ENUM.SELECTOR.id:
        case COMPONENT_ENUM.CHECKBOX.id:
          this.editDialogContent = SelectorFieldEditor
          break
        case COMPONENT_ENUM.TABLE.id:
          this.editDialogContent = TableFieldEditor
          break
      }

      this.editDialogIsOpen = true
    },
    handleCancel() {
      this.selectedComponent = null
      this.selectedItem = undefined
      this.deleteDialogIsOpen = false
      this.editDialogIsOpen = false
      this.editDialogContent = null
    },
    handleError(message) {
      this.send(message || this.$t("messages.error.failed_admin", { role: "INSPECTOR" }), "error")
    },
    handleSuccess() {
      this.handleCancel()
      this.$nextTick(() => {
        this.fetchFieldTypes()
        this.send(this.$t("messages.success.operation"), "success")
      })
    },
    handleDelete() {
      FieldTypeService.delete(this.selectedItem.id)
        .then(() => {
          this.handleSuccess()
        })
        .catch(error => {
          const message = error?.response.data?.state
          this.handleError(message)
        })
    },
    async duplicateField(item) {
      FieldTypeService.duplicate(item.id).then(response => {
        this.send(
          this.$t("messages.success.duplicated", { entity: this.$t("commons.field") }),
          "success"
        )
        this.fetchFieldTypes()
      })
    },
    get_status(item) {
      if (item == "EDITABLE") {
        return this.$t("commons.editable")
      } else if (item == "PUBLISHED") {
        return this.$t("commons.published")
      } else if (item == "REVOKED") {
        return this.$t("commons.revoked")
      } else if (item == "SIGNED") {
        return this.$t("commons.signed")
      } else {
        return this.$t("commons.locked")
      }
    },
    get_type(item) {
      if (item.toLowerCase() == "selector") {
        return this.$t("commons.selector")
      } else if (item.toLowerCase() == "table") {
        return this.$t("commons.table")
      } else if (item.toLowerCase() == "checkbox") {
        return this.$t("commons.checkbox")
      } else if (item.toLowerCase() == "textfield") {
        return this.$t("commons.textfield")
      } else if (item.toLowerCase() == "imagefield") {
        return this.$t("commons.imagefield")
      } else if (item.toLowerCase() == "textarea") {
        return this.$t("commons.textarea")
      } else {
        return this.$t("commons.datefield")
      }
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    }
  }
}
</script>
